import { NextSeo } from 'next-seo';
import Head from 'next/head';

//screens
import { DocumentCollectionAndFiling } from '@/screens/Latam/Agents/DocumentCollectionAndFiling';
import { TrackingAndStatusUpdates } from '@/screens/Latam/Agents/TrackingAndStatusUpdates';
import { TenderFailures } from '@/screens/Latam/Agents/TenderFailures';
import { ProactiveLoadAudits } from '@/screens/Latam/Agents/ProactiveLoadAudits';
import { ClaimsProcessing } from '@/screens/Latam/Agents/ClaimsProcessing';
import { SchedulingAndReScheduling } from '@/screens/Latam/Agents/SchedulingAndReScheduling';

//data-helpers
import { JsonLd, Organization } from '@/datafiles/micro-data';
import { buildFaqSchema } from '@/datafiles/micro-data/faqPage';
import { getLatamDictionary } from '@/datafiles/languages/latam';
import { buildLatamChrome } from '@/datafiles/nav/latam';
import { OgLocaleAlternates } from '@/components/OgLocaleAlternates';
import { getLocalizedSeo } from '@/datafiles/i18n/seo';
import { getPathFor } from '@/datafiles/i18n/translatedPages';

// Dictionary key -> registry id (pages.json) and the screen that renders it.
const AGENTS = {
  document_collection_and_filing: {
    id: 'latam-agent-document-collection',
    Screen: DocumentCollectionAndFiling,
  },
  tracking_and_status_updates: {
    id: 'latam-agent-tracking',
    Screen: TrackingAndStatusUpdates,
  },
  tender_failures: {
    id: 'latam-agent-tender-failures',
    Screen: TenderFailures,
  },
  proactive_load_audits: {
    id: 'latam-agent-load-audits',
    Screen: ProactiveLoadAudits,
  },
  claims_processing: { id: 'latam-agent-claims', Screen: ClaimsProcessing },
  scheduling_and_re_scheduling: {
    id: 'latam-agent-scheduling',
    Screen: SchedulingAndReScheduling,
  },
};

const pathOf = (id, locale) => getPathFor(id, locale, { includePlanned: true });

// One body for every LATAM agent route (e.g. /en/ai/tracking-status-updates/,
// /pt-br/ia/rastreamento-de-cargas/): each route file picks locale + agent.
export function LatamAgentPage({ locale, agentKey }) {
  const { agents, agents_shared: shared, home } = getLatamDictionary(locale);
  const agent = agents[agentKey];
  const { id, Screen } = AGENTS[agentKey];
  const { nav, footer } = buildLatamChrome(locale);
  const seo = getLocalizedSeo(id, locale);

  const contactHref = `${pathOf('latam-contact', locale)}#contact-form`;

  const content = {
    hero: {
      slug: agent.hero.slug,
      title: agent.hero.title,
      description: agent.hero.description,
      metric: agent.hero.metric,
      metricLabel: agent.hero.metric_label,
    },
    watchItRun: {
      title: agent.watch_it_run.title,
      stageHeadTitle: agent.watch_it_run.stage_head_title,
      stageHeadMeta: agent.watch_it_run.stage_head_meta,
      rows: agent.watch_it_run.rows,
      summaryText: agent.watch_it_run.summary_text,
      agentBadge: agent.watch_it_run.agent_badge,
      escalationTitle: agent.watch_it_run.escalation_title,
      escalationText: agent.watch_it_run.escalation_text,
    },
    howItWorks: {
      stepsTitleWhite: agent.how_it_works.steps_title_white,
      stepsTitleGradient: agent.how_it_works.steps_title_gradient,
      steps: agent.how_it_works.steps,
      outcomes: agent.how_it_works.outcomes,
    },
    lineupCards: agent.lineup_cards.map((card) => ({
      slug: card.slug,
      title: card.title,
      description: card.description,
      href: pathOf(AGENTS[card.agent].id, locale),
    })),
    agentsShared: {
      hero: {
        eyebrowPrefix: shared.hero.eyebrow_prefix,
        ctaLabel: shared.hero.cta_label,
        homeHref: pathOf('latam-platform', locale),
        aiHref: pathOf('latam-home', locale),
        ctaHref: contactHref,
      },
      watchItRunYourFreight: {
        kicker: shared.watch_it_run_your_freight.kicker,
        statusWorking: shared.watch_it_run_your_freight.status_working,
        statusResolved: shared.watch_it_run_your_freight.status_resolved,
        summaryLabel: shared.watch_it_run_your_freight.summary_label,
        sessionLabel: shared.watch_it_run_your_freight.session_label,
        replayLabel: shared.watch_it_run_your_freight.replay_label,
      },
      howItWorks: {
        kickerWhatAgentDoes: shared.how_it_works.kicker_what_agent_does,
        kickerWhatChanges: shared.how_it_works.kicker_what_changes,
        outcomesTitle: shared.how_it_works.outcomes_title,
      },
      runsOnYourStack: {
        kicker: shared.runs_on_your_stack.kicker,
        title: shared.runs_on_your_stack.title,
        titleHighlight: shared.runs_on_your_stack.title_highlight,
        description: shared.runs_on_your_stack.description,
        diagram: {
          ariaLabel: shared.runs_on_your_stack.diagram.aria_label,
          sorLine1: shared.runs_on_your_stack.diagram.sor_line1,
          sorLine2: shared.runs_on_your_stack.diagram.sor_line2,
          sorSub: shared.runs_on_your_stack.diagram.sor_sub,
          agentTitle: shared.runs_on_your_stack.diagram.agent_title,
          agentSub: shared.runs_on_your_stack.diagram.agent_sub,
          integrations: shared.runs_on_your_stack.diagram.integrations,
        },
      },
      theRestOfTheLineup: {
        kicker: shared.the_rest_of_the_lineup.kicker,
        title: shared.the_rest_of_the_lineup.title,
        titleHighlight: shared.the_rest_of_the_lineup.title_highlight,
        lede: shared.the_rest_of_the_lineup.lede,
        readMoreLabel: shared.the_rest_of_the_lineup.read_more_label,
      },
      endCta: {
        title: shared.end_cta.title,
        titleHighlight: shared.end_cta.title_highlight,
        copyPrefix: shared.end_cta.copy_prefix,
        copySuffix: shared.end_cta.copy_suffix,
        ctaLabel: shared.end_cta.cta_label,
        ctaHref: contactHref,
      },
    },
  };

  const faqContent = agent.faq
    ? { title: home.faq.title, items: agent.faq.items }
    : undefined;

  return (
    <>
      <Head>
        <meta name="keywords" content={agent.meta_keywords} />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG === 'true'}
        noindex={process.env.NOINDEX_FLAG === 'true'}
        title={agent.meta_title}
        description={agent.meta_description}
        canonical={seo.canonical}
        languageAlternates={seo.languageAlternates}
        openGraph={{ url: seo.canonical, locale: seo.ogLocale }}
      />
      <OgLocaleAlternates locales={seo.ogLocaleAlternates} />
      <JsonLd data={Organization} />
      {faqContent && <JsonLd data={buildFaqSchema(faqContent.items)} />}
      <Screen
        content={content}
        nav={nav}
        footer={footer}
        faqContent={faqContent}
      />
    </>
  );
}
